<template>
    <div v-if="city" class="city-next-root app-screen-bg">
        <div class="city-next-banner" :style="bgStyle" ref="banner">
            <span class="city-next-title-pill">{{ $t('common.cityNames.' + city.name) }}</span>
        </div>
        <div class="city-next-content">
            <v-row class="ma-0 pa-2 city-next-grid" dense>
                <v-col v-for="tile in tiles" :key="tile.key" :cols="colSpan" class="text-center city-next-col">
                    <NextCircleTile
                        v-if="!tile.empty"
                        :img="tile.img"
                        :title="tile.title"
                        :steps="tile.steps || null"
                        :disabled="!!tile.disabled"
                        :img-greyscale="!!tile.imgGreyscale"
                        :badge-img="tile.badgeImg || ''"
                        :badge-greyscale="!!tile.badgeGreyscale"
                        :corner-icon="tile.cornerIcon || ''"
                        :show-star="!!tile.showStar"
                        @click="tile.action && tile.action()"
                    />
                </v-col>
            </v-row>
        </div>

        <CityHealDialog
            v-model="showHealDialog"
            :trainer-id="trainerPosition.trainer.id"
            :step-cost="healStepCost"
            :is-loading="healingInProgress"
            @heal="doHeal"
        />
        <CityTravelDialog
            v-model="showTravelDialog"
            :cities="citiesForDialog"
            :current-city-name="trainerPosition.city.name"
            :steps="minTravelSteps"
            :trainer-badges="trainerPosition.trainer.badges"
            @select-city="doTravelToCity"
        />
        <OakDialog
            v-model="showOakDialog"
            :trainer-id="trainerPosition.trainer.id"
            @team-changed="refreshTeamSheet"
        />
        <OfficerJennyDialog
            v-model="showJennyDialog"
            :trainer-id="trainerPosition.trainer.id"
            :position="trainerPosition"
            @team-changed="refreshTeamSheet"
        />
        <MewtwoChallengeDialog
            v-model="showMewtwoDialog"
            :available-steps="steps"
            :is-loading="isLoading"
            @accept="mewtwoChallenge"
        />
        <RegisterLeagueDialog
            v-model="showRegisterLeagueDialog"
            :having-pokemons-count="trainerPosition.trainer.havingPokemonsCount"
            :is-loading="isLoading"
            @accept="confirmRegisterLeague"
        />
        <ConfirmDialog ref="confirmDialog"/>
    </div>
</template>

<script>
    // Hlavní obrazovka mezi souboji/cestami (position.type CITY) - dlaždicový
    // grid akcí, logika/API přes sdílený journeyApi modul. Design/rozhodnutí
    // viz claude/components/journey-city.md.
    //
    // OakDialog/OfficerJennyDialog zůstávají popupy nad CITY (nevolají SETTYPE
    // `visit-oak`/`visit-officer-jenny`) - žádný pokemon-management endpoint
    // (leave-pokemon/pokemon-buy-skill/evolve-pokemon/PATCH pokemon) position.type
    // nekontroluje, takže je to bezpečné.
    import {mapActions, mapGetters} from 'vuex';
    import * as journeyApi from '@/api/journeyApi';
    import {Positions} from '@/utils/positionEnum';
    import NextCircleTile from './pieces/NextCircleTile';
    import CityHealDialog from './CityHealDialog';
    import CityTravelDialog from './CityTravelDialog';
    import OakDialog from './OakDialog';
    import OfficerJennyDialog from './OfficerJennyDialog';
    import MewtwoChallengeDialog from './MewtwoChallengeDialog';
    import RegisterLeagueDialog from './RegisterLeagueDialog';
    import ConfirmDialog from '@/components/utils/ConfirmDialog';
    import {computeGridCols} from '@/utils/gridLayout';

    const HEAL_STEP_COST = 2;
    const MIN_TRAVEL_STEPS = 3;
    const LEAGUE_DAYS_CYCLE = 340;
    // Jediný zdroj pravdy pro VŠECHNY možné dlaždice, i ty co se v daném
    // stavu nezobrazí (prázdné sloty mají {empty: true}) - grid tak má vždy
    // stejný počet políček a stejnou pozici, viz journey-city.md.
    const ALL_TILE_KEYS = ['oak', 'heal', 'wild', 'train', 'stadion', 'mewtwo', 'jenny', 'league'];
    // Musí odpovídat padding-bottom v .app-screen-bg (_card.sass).
    const TEAM_SHEET_HANDLE_HEIGHT = 22;

    export default {
        name: 'CityNext',
        components: {NextCircleTile, CityHealDialog, CityTravelDialog, OakDialog, OfficerJennyDialog, MewtwoChallengeDialog, RegisterLeagueDialog, ConfirmDialog},
        props: {
            trainerPosition: {type: Object, default: null},
        },
        data() {
            return {
                cities: [],
                showHealDialog: false,
                showTravelDialog: false,
                showOakDialog: false,
                showJennyDialog: false,
                showMewtwoDialog: false,
                showRegisterLeagueDialog: false,
                // Vlastní flag pro celou dobu doHeal() (request + GIF padding) -
                // sdílený journey/isLoading getter spadne zpět na false hned po
                // odpovědi ze serveru, ne až po dohrání GIFu.
                healingInProgress: false,
                healStepCost: HEAL_STEP_COST,
                minTravelSteps: MIN_TRAVEL_STEPS,
                containerWidth: 0,
                containerHeight: 0,
                bannerHeight: 0,
            }
        },
        computed: {
            ...mapGetters({
                isLoading: 'journey/isLoading',
                steps: 'journey/steps',
                trainerHasOakAction: 'journey/trainerHasOakAction',
                user: 'user/item',
                city: 'city/item',
            }),
            bgStyle() {
                return {backgroundImage: `url(${require(`@/assets/pic/city/${this.trainerPosition.city.id}.png`)})`};
            },
            // `cities` samo zůstává jen seznam dosažitelných cílů (train dlaždice
            // v hlavním gridu se podle toho schovává, viz loadCities()) - aktuální
            // město se přidává jen tady, jako zašedlá dlaždice. Řazeno sestupně
            // podle level (stejně jako API `order[id]=desc`, id~level 1:1) -
            // natvrdo přilepené "You're here" na začátek by rozbilo pořadí čísel.
            citiesForDialog() {
                return [
                    {id: this.city.id, name: this.city.name, img: this.city.id + '.png', level: this.city.level, badgeId: this.city.stadion ? this.city.stadion.badge.id : null, disabled: true, belowLabel: this.$t('journey.cityNext.youAreHere')},
                    ...this.cities,
                ].sort((a, b) => b.level - a.level);
            },
            hasStolenPokemons() {
                return this.trainerPosition.trainer.stolenPokemonsCount > 0;
            },
            oakTile() {
                return this.trainerPosition.city.name === 'Pallet Town'
                    ? {img: 'oaks_house.png', title: this.$t('journey.cityNext.oakTitleVisit')}
                    : {img: 'call_oak.png', title: this.$t('journey.cityNext.oakTitleCall')};
            },
            remainingLeagueDays() {
                let remaining = LEAGUE_DAYS_CYCLE - this.trainerPosition.days;
                while (remaining < 0) {
                    remaining += 360;
                }
                return remaining;
            },
            championshipTile() {
                if (!(this.trainerPosition.trainer.badges.length >= 8 && !this.trainerPosition.trainer.leagueChampion)) {
                    return null;
                }
                if (this.remainingLeagueDays > 20) {
                    return {title: this.$t('journey.cityNext.championshipTitle'), disabled: false};
                }
                return {title: this.$t('journey.cityNext.championshipTitle'), disabled: true};
            },
            showMewtwoTile() {
                return this.trainerPosition.trainer.leagueChampion
                    && this.trainerPosition.city.name === 'Viridian City'
                    && !this.trainerOwnBadgeRocket();
            },
            stadionTile() {
                const stadion = this.trainerPosition.city.stadion;
                if (!stadion) {
                    return null;
                }
                return {
                    key: 'stadion',
                    img: require(`@/assets/pic/stadion/${stadion.badge.id}.png`),
                    imgGreyscale: this.trainerOwnBadge(),
                    badgeImg: require(`@/assets/pic/badge/${stadion.badge.id}.png`),
                    badgeGreyscale: !this.trainerOwnBadge(),
                    title: this.$t('common.stadionNames.' + stadion.name),
                    steps: this.trainerOwnBadge() ? 0 : 3,
                    disabled: this.isLoading,
                    action: this.trainerOwnBadge() ? this.visitTrainer : this.stadionFight,
                };
            },
            tiles() {
                const map = {};

                map.oak = {
                    key: 'oak',
                    img: require(`@/assets/pic/web/${this.oakTile.img}`),
                    title: this.oakTile.title,
                    cornerIcon: 'mdi-pokeball',
                    // hvězdička = evolvable pokémon odložený u Oaka (roadmap.md bod 5)
                    showStar: this.trainerHasOakAction,
                    action: () => { this.showOakDialog = true; },
                };
                map.heal = {
                    key: 'heal',
                    img: require('@/assets/pic/web/pokecenter.png'),
                    title: this.$t('journey.cityNext.healTitle'),
                    cornerIcon: 'mdi-heart-pulse',
                    disabled: this.steps < this.healStepCost,
                    action: () => {
                        if (this.steps >= this.healStepCost) {
                            this.showHealDialog = true;
                        }
                    },
                };
                map.wild = {
                    key: 'wild',
                    img: require('@/assets/pic/web/forest.png'),
                    title: this.$t('journey.cityNext.wildTitle'),
                    cornerIcon: 'mdi-shoe-print',
                    disabled: this.isLoading,
                    action: this.travel,
                };
                // Prázdný slot, dokud není odemčené aspoň jedno jiné město
                // (this.cities je stejný seznam jako CityTravelDialog).
                if (this.cities.length > 0) {
                    map.train = {
                        key: 'train',
                        img: require('@/assets/pic/web/travel/train-station-town.png'),
                        title: this.$t('journey.cityNext.trainTitle'),
                        cornerIcon: 'mdi-train',
                        disabled: this.steps < this.minTravelSteps,
                        action: () => {
                            if (this.steps >= this.minTravelSteps) {
                                this.showTravelDialog = true;
                            }
                        },
                    };
                }

                if (this.stadionTile) {
                    map.stadion = this.stadionTile;
                }

                if (this.hasStolenPokemons) {
                    map.jenny = {
                        key: 'jenny',
                        img: require('@/assets/pic/web/Officer_Jenny_avatar.png'),
                        title: this.$t('journey.cityNext.officerJennyTitle'),
                        cornerIcon: 'mdi-magnify',
                        action: () => { this.showJennyDialog = true; },
                    };
                }
                if (this.championshipTile) {
                    map.league = {
                        key: 'league',
                        img: require('@/assets/pic/web/thumb/league_stadion.png'),
                        title: this.championshipTile.title,
                        cornerIcon: 'mdi-trophy',
                        disabled: this.championshipTile.disabled || this.isLoading,
                        action: this.registerLeague,
                    };
                }
                if (this.showMewtwoTile) {
                    map.mewtwo = {
                        key: 'mewtwo',
                        img: require('@/assets/pic/web/mewtwo_chalenge.png'),
                        title: this.$t('journey.cityNext.mewtwoTitle'),
                        cornerIcon: 'mdi-star',
                        action: () => { this.showMewtwoDialog = true; },
                    };
                }

                return ALL_TILE_KEYS.map((key) => map[key] || {key, empty: true});
            },
            // Zkouší od nejmenšího počtu sloupců (2) nahoru - první, kam se
            // všechny řádky vejdou na výšku bez scrollování, vyhrává. `n` je
            // vždy tiles.length (i neaktivní/empty sloty), ne počet aktivních
            // dlaždic - grid má mít stabilní pozici bez ohledu na to, co je
            // zrovna aktivní.
            gridCols() {
                return computeGridCols({
                    n: this.tiles.length,
                    containerWidth: this.containerWidth,
                    containerHeight: this.containerHeight,
                    extraHeight: this.bannerHeight + TEAM_SHEET_HANDLE_HEIGHT,
                    rowGap: 10,
                });
            },
            colSpan() {
                return 12 / this.gridCols;
            },
        },
        watch: {
            trainerPosition: {
                immediate: true,
                handler(newPosition) {
                    if (newPosition) {
                        this.loadCities();
                    }
                },
            },
            // `city` je asynchronně načítaný Vuex getter (ne prop) - mounted()
            // často proběhne ještě před tím, než je pravdivý a `v-if="city"`
            // na kořeni vůbec něco vyrenderuje (do té doby getBoundingClientRect()
            // vrací nuly). Bez tohodle by gridCols() zůstal navždy na fallbacku.
            city() {
                this.measure();
            },
        },
        created() {
            if (this.user && this.user.id) {
                this.trainerInfoMessages(this.user.id);
            }
        },
        mounted() {
            this.measure();
            window.addEventListener('resize', this.measure);
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.measure);
        },
        methods: {
            ...mapActions({
                positionPost: 'journey/positionPost',
                fetchData: 'journey/fetchObject',
                getCity: 'city/loadItem',
                trainerInfoMessages: 'trainer/loadInfoMessages',
                refreshActiveTeam: 'pokemon/refreshActiveTeam',
            }),
            // `this.$el.clientHeight` je nespolehlivý, protože rodičovský
            // <v-card> v Journey.vue nemá explicitní výšku.
            measure() {
                this.$nextTick(() => {
                    if (!this.$el || !this.$el.getBoundingClientRect) {
                        return;
                    }
                    const rect = this.$el.getBoundingClientRect();
                    this.containerWidth = rect.width;
                    this.containerHeight = window.innerHeight - rect.top;
                    if (this.$refs.banner) {
                        this.bannerHeight = this.$refs.banner.offsetHeight;
                    }
                });
            },
            travel() {
                this.positionPost(journeyApi.travel(this.trainerPosition.id));
            },
            doTravelToCity(cityId) {
                this.positionPost(journeyApi.travel(this.trainerPosition.id, cityId, 3));
            },
            async doHeal() {
                // Popup zůstává otevřený min. GIF_DURATION_MS, ať má "isLoading"
                // stav (kolečko + GIF) šanci se vůbec ukázat - GIF sám má jen
                // 1200ms/12 snímků bez loopu, pak zamrzne na posledním snímku.
                // Při pomalejší odpovědi serveru čekáme přirozeně déle, žádná
                // umělá prodleva navíc.
                const GIF_DURATION_MS = 2000;
                const start = Date.now();
                this.healingInProgress = true;
                await this.positionPost(journeyApi.pokeCenter(this.user.position.id));
                const elapsed = Date.now() - start;
                if (elapsed < GIF_DURATION_MS) {
                    await new Promise((resolve) => setTimeout(resolve, GIF_DURATION_MS - elapsed));
                }
                this.healingInProgress = false;
                this.showHealDialog = false;
                // HP ve spodní liště (TeamBottomSheet.vue) je z vlastního
                // nezávislého fetch - bez explicitního refreshe by po heal dál
                // ukazoval staré (zraněné) hodnoty.
                this.refreshTeamSheet();
            },
            // OakDialog.vue načítá všechny pokémony do vlastního lokálního
            // stavu (ne sdíleného pokemon/items), takže změna týmu tam se sem
            // musí poslat explicitně přes pokemon/refreshActiveTeam.
            refreshTeamSheet() {
                this.refreshActiveTeam();
            },
            stadionFight() {
                if (!this.trainerOwnBadge()) {
                    this.positionPost(journeyApi.meetStadionTrainer(this.trainerPosition.id, this.city.stadion.stadionTrainer.id));
                }
            },
            visitTrainer() {
                this.positionPost(journeyApi.setPositionType(this.trainerPosition.id, Positions.FIGHT_STADION_TRAINER_WIN));
            },
            mewtwoChallenge() {
                if (!this.trainerOwnBadgeRocket()) {
                    this.positionPost(journeyApi.meetMewtwo());
                }
            },
            async registerLeague() {
                if (this.championshipTile && this.championshipTile.disabled) {
                    return;
                }
                // Registrace jde jen z Viridian City, ale dlaždice se zobrazuje
                // ve všech městech - klik odjinud jen informuje, kam jít.
                if (this.trainerPosition.city.name !== 'Viridian City') {
                    await this.$refs.confirmDialog.open(
                        this.$t('journey.cityNext.championshipCityOnly', {city: this.$t('common.cityNames.Viridian City')}),
                        {okOnly: true}
                    );
                    return;
                }
                this.showRegisterLeagueDialog = true;
            },
            confirmRegisterLeague() {
                this.positionPost(journeyApi.registerLeague(this.trainerPosition.id));
            },
            loadCities() {
                this.getCity(this.trainerPosition.city.id).then(() => {
                    let filter = '';
                    if (this.trainerPosition.nextCity !== null && this.trainerPosition.nextCity !== undefined) {
                        filter = '&id[lte]=' + this.trainerPosition.nextCity.id;
                    }
                    this.fetchData(journeyApi.citiesList(filter)).then((data) => {
                        this.cities = data['member']
                            .filter((c) => c.id !== this.city.id)
                            .filter((c) => !(this.trainerPosition.nextCity && c.id === this.trainerPosition.nextCity.id))
                            .map((c) => ({id: c.id, name: c.name, img: c.id + '.png', level: c.level, badgeId: c.stadion ? c.stadion.badge.id : null}));
                    });
                });
            },
            trainerOwnBadge() {
                if (!this.trainerPosition?.trainer?.badges || !this.city?.stadion?.badge) {
                    return false;
                }
                return this.trainerPosition.trainer.badges.some((badge) => badge.id === this.city.stadion.badge.id);
            },
            trainerOwnBadgeRocket() {
                return this.trainerPosition.trainer.badges.some((badge) => badge.id === 9);
            },
        },
    }
</script>

<style scoped>
.city-next-root {
    height: 100%;
    overflow: auto;
    /* pozadí i rezervované místo pro TeamBottomSheet dole řídí sdílená třída
       .app-screen-bg (assets/styles/_card.sass), sjednocená napříč
       City/Wild/You/Players/Pokedex/Skills/Rules. */
}
.city-next-banner {
    position: relative;
    height: 150px;
    background-size: cover;
    background-position: center;
    display: flex;
    /* pilulka nahoru NA obrázku, ne dole - stejná konvence i u travel/heal popupů */
    align-items: flex-start;
    justify-content: center;
    padding-top: 10px;
}
.city-next-title-pill {
    display: inline-block;
    background: rgba(0,0,0,0.65);
    color: #fff;
    font-size: 24px;
    font-weight: 700;
    padding: 6px 24px;
    border-radius: 20px;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.6);
}
.city-next-content {
    padding-top: 12px;
}
.city-next-grid {
    row-gap: 10px;
}
.city-next-col {
    padding: 0 4px !important;
}
</style>
